import { TOOL_TYPE } from 'core/chatbot/utils/parsers/ToolType';
import { logToSentry } from 'core/utils/sentry';
import mermaid from 'mermaid';
import PropTypes from 'prop-types';
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import useAnalytics from '../../../../hooks/useAnalytics';
import { EVENT_NAMES } from '../../../constants/eventNames';
import { downloadSvgFromContainer } from '../../../utils/downloadImage';
import ChatButton from '../ChatButton';
import DiagramError from './DiagramError';
import './DiagramMermaid.scss';

const DiagramMermaid = forwardRef(function DiagramMermaid(
  { applyStyles, className, config, conversationId, delay, messageId, mermaidCode, onError, onSuccess, renderError, t },
  ref
) {
  const containerRef = useRef(null);
  const [renderState, setRenderState] = useState('idle'); // 'idle', 'loading', 'success', 'error'
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [showDownloadButton, setShowDownloadButton] = useState(false);
  const instanceId = useRef(`mermaid-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`);

  // Analytics hook
  const { trackEvent } = useAnalytics();

  // Refs para funciones - evita re-renders innecesarios
  const onErrorRef = useRef(onError);
  const onSuccessRef = useRef(onSuccess);
  const applyStylesRef = useRef(applyStyles);

  // Actualizar refs cuando las props cambien
  useEffect(() => {
    onErrorRef.current = onError;
    onSuccessRef.current = onSuccess;
    applyStylesRef.current = applyStyles;
  });

  // Memoizar configuración de Mermaid
  const mermaidConfig = useMemo(
    () => ({
      startOnLoad: false,
      logLevel: 'fatal',
      theme: 'default',
      suppressErrorRendering: true,
      deterministicIds: false,
      maxTextSize: 50000,
      ...config,
      // Last, so no caller can reopen `click` callbacks and unsanitized link URLs
      securityLevel: 'strict',
    }),
    [config]
  );

  async function downloadAsPngWithExportRender() {
    const exportContainer = document.createElement('div');
    exportContainer.style.position = 'fixed';
    exportContainer.style.left = '0';
    exportContainer.style.top = '0';
    exportContainer.style.zIndex = '-1';
    exportContainer.style.pointerEvents = 'none';

    try {
      // Render específico para export: desactivamos htmlLabels (foreignObject) para evitar truncado de texto.
      // No queremos afectar al render actual en pantalla, así que usamos un contenedor temporal.
      mermaid.initialize({
        ...mermaidConfig,
        flowchart: {
          ...(mermaidConfig.flowchart || {}),
          htmlLabels: false,
        },
      });

      const exportDiagramId = `mermaid-export-${instanceId.current}-${Date.now()}`;
      const { svg } = await mermaid.render(exportDiagramId, mermaidCode);

      exportContainer.innerHTML = svg;
      document.body.appendChild(exportContainer);

      await downloadSvgFromContainer(exportContainer, {
        filename: `mermaid_diagram-${Date.now()}`,
        scale: 2,
        backgroundColor: 'white',
        padding: 20,
      });
    } finally {
      if (exportContainer.parentNode) {
        document.body.removeChild(exportContainer);
      }

      // Restaurar configuración para no impactar renders posteriores.
      mermaid.initialize(mermaidConfig);
    }
  }

  // Función para descargar el diagrama como PNG usando la utilidad
  const downloadAsPNG = async () => {
    try {
      await downloadAsPngWithExportRender();

      // Track analytics event for diagram download
      try {
        await trackEvent(EVENT_NAMES.ARTIFACT_DOWNLOADED, {
          type: TOOL_TYPE.MERMAID_DIAGRAM,
        });
      } catch (analyticsError) {
        console.error('Failed to track diagram download event:', analyticsError);
        // Don't throw the error to avoid breaking the download functionality
      }
    } catch (error) {
      console.error('Error al descargar la imagen:', error);
      logToSentry('error', 'Mermaid diagram download failed', {
        tags: {
          feature: 'mermaid-diagram',
          component: 'DiagramMermaid',
        },
        extra: {
          mermaidCode,
          conversationId,
          messageId,
          errorMessage: error?.message,
        },
        error: error instanceof Error ? error : undefined,
      });
    }
  };

  // Handlers para el hover
  const handleMouseEnter = () => {
    if (renderState === 'success') {
      setShowDownloadButton(true);
    }
  };

  const handleMouseLeave = () => {
    setShowDownloadButton(false);
  };

  useImperativeHandle(ref, () => ({
    container: containerRef.current,
    renderState,
    downloadAsPNG,
    retry: () => {
      setRenderState('idle');
      setError(null);
      setRetryCount((prev) => prev + 1); // Disparar re-render
    },
  }));

  useEffect(() => {
    if (!containerRef.current || !mermaidCode || renderState === 'loading') return;

    const renderDiagram = async () => {
      try {
        setRenderState('loading');
        setError(null);

        // Configurar Mermaid
        mermaid.initialize(mermaidConfig);

        // Limpiar contenido anterior
        containerRef.current.innerHTML = '';

        // Generar ID único para el diagrama
        const diagramId = `mermaid-${instanceId.current}-${Date.now()}`;

        // Renderizar diagrama
        const { svg } = await mermaid.render(diagramId, mermaidCode);

        // Verificar que el contenedor sigue disponible
        if (!containerRef.current) return;

        // Insertar el SVG
        containerRef.current.innerHTML = svg;

        // Aplicar estilos personalizados al SVG si es necesario
        if (applyStylesRef.current) {
          const svgElement = containerRef.current.querySelector('svg');
          if (svgElement) {
            applyStylesRef.current(svgElement);
          }
        }

        setRenderState('success');
        onSuccessRef.current?.();
      } catch (err) {
        setRenderState('error');
        setError(err);
        logToSentry('error', 'Mermaid diagram rendering failed', {
          tags: {
            feature: 'mermaid-diagram',
            component: 'DiagramMermaid',
          },
          extra: {
            mermaidCode,
            conversationId,
            messageId,
            errorMessage: err?.message,
            instanceId: instanceId.current,
          },
          error: err instanceof Error ? err : undefined,
        });

        if (containerRef.current) {
          containerRef.current.innerHTML = '';
        }

        onErrorRef.current?.(err);
      }
    };

    // Aplicar delay si se especifica
    if (delay > 0) {
      const timeoutId = setTimeout(renderDiagram, delay);
      return () => clearTimeout(timeoutId);
    } else {
      renderDiagram();
    }
  }, [mermaidCode, mermaidConfig, delay, retryCount]); // ✅ Dependencias optimizadas

  // Renderizar contenido basado en el estado
  if (renderState === 'error') {
    return <DiagramError error={error} mermaidCode={mermaidCode} renderCustomError={renderError} t={t} />;
  }

  return (
    <div className={`diagram-mermaid ${className || ''}`} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <div ref={containerRef} />
      {showDownloadButton && (
        <ChatButton
          className="diagram-mermaid__download-btn"
          size="large"
          variant="outlined"
          color="brand"
          icon="download"
          iconPosition="right"
          onClick={downloadAsPNG}
        >
          {t('chatbot:Download image') || 'Descargar imagen'}
        </ChatButton>
      )}
    </div>
  );
});

DiagramMermaid.propTypes = {
  applyStyles: PropTypes.func,
  className: PropTypes.string,
  config: PropTypes.object,
  conversationId: PropTypes.string,
  delay: PropTypes.number,
  mermaidCode: PropTypes.string.isRequired,
  messageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onError: PropTypes.func,
  onSuccess: PropTypes.func,
  renderError: PropTypes.func,
  t: PropTypes.func,
};

DiagramMermaid.defaultProps = {
  applyStyles: null,
  className: '',
  config: {},
  conversationId: null,
  delay: 0,
  messageId: null,
  onError: null,
  onSuccess: null,
  renderError: null,
  t: () => '',
};

export default DiagramMermaid;
